import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { ShieldCheck, FileBarChart, MessageSquare, Send, Loader2, AlertTriangle } from "lucide-react";
import { LineChart, Line, ResponsiveContainer, Tooltip, XAxis, YAxis, CartesianGrid, BarChart, Bar } from "recharts";
import { api } from "@/lib/api";
import { PageHeader, SectionCard, KpiCard } from "@/components/Primitives";

const WINDOWS = [
  { label: "30-day", value: 30 },
  { label: "90-day", value: 90 },
];

const TABS = [
  { key: "prior-auth", label: "Module A · Prior Auth", icon: ShieldCheck, color: "#4A0E8F" },
  { key: "cpt", label: "Module B · CPT Acceptance", icon: FileBarChart, color: "#0D7377" },
  { key: "aria", label: "Module C · ARIA Queries", icon: MessageSquare, color: "#9B59B6" },
  { key: "referrals", label: "Module D · Referrals", icon: Send, color: "#7E22CE" },
];

const ENDPOINTS = {
  "prior-auth": "/outcomes/prior-auth",
  "cpt": "/outcomes/cpt-codes",
  "aria": "/outcomes/aria-queries",
  "referrals": "/outcomes/referrals",
};

const TICK_STYLE = { fontSize: 11, fill: "#6b7280" };

export default function Outcomes() {
  const [params, setParams] = useSearchParams();
  const initialTab = params.get("tab") || "prior-auth";
  const initialWindow = parseInt(params.get("window"), 10) || 30;
  const [tab, setTab] = useState(initialTab);
  const [window, setWindow] = useState(initialWindow);
  const [data, setData] = useState(null);
  const [dataTab, setDataTab] = useState(null);  // tracks which tab the data belongs to
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    setLoading(true);
    setParams({ tab, window: String(window) }, { replace: true });
    api.get(`${ENDPOINTS[tab]}?window=${window}`)
      .then(r => { setData(r.data); setDataTab(tab); })
      .finally(() => setLoading(false));
  }, [tab, window]);  // eslint-disable-line

  // Render only when data shape matches the current tab — prevents stale-payload crashes.
  const ready = !loading && data && dataTab === tab;

  const selectTab = (key) => {
    if (key === tab) return;
    setData(null);
    setDataTab(null);
    setTab(key);
  };

  return (
    <>
      <PageHeader title="Outcome Data Tracking" subtitle="Closed-loop outcomes across PA, CPT, ARIA & Referrals — de-identified, physician-scoped." testid="outcomes-header"
        actions={
          <div className="inline-flex border border-brand rounded-md overflow-hidden text-xs" data-testid="outcomes-window">
            {WINDOWS.map(w => (
              <button key={w.value} onClick={() => setWindow(w.value)}
                className={`px-3 py-1.5 transition-colors ${window === w.value ? "bg-brand-primary text-white" : "bg-white text-brand-muted hover:bg-brand-bg"}`}
                data-testid={`outcomes-window-${w.value}`}>
                {w.label}
              </button>
            ))}
          </div>
        } />

      <div className="flex gap-2 mb-5 overflow-x-auto pb-1" data-testid="outcomes-tabs">
        {TABS.map(t => {
          const Ico = t.icon;
          const active = t.key === tab;
          return (
            <button key={t.key} onClick={() => selectTab(t.key)}
              className={`shrink-0 px-4 py-2.5 rounded-lg border text-sm font-medium inline-flex items-center gap-2 transition-all ${active ? "bg-brand-primary text-white border-brand-primary shadow-sm" : "bg-white border-brand text-brand-muted hover:bg-brand-bg"}`}
              data-testid={`outcomes-tab-${t.key}`}>
              <Ico className="w-4 h-4" />
              {t.label}
            </button>
          );
        })}
      </div>

      {loading && (
        <div className="flex items-center gap-2 text-sm text-brand-muted py-12 justify-center">
          <Loader2 className="w-4 h-4 animate-spin" /> Loading outcomes for the {window}-day window…
        </div>
      )}

      {ready && tab === "prior-auth" && <PriorAuthPanel data={data} window={window} />}
      {ready && tab === "cpt" && <CptPanel data={data} window={window} />}
      {ready && tab === "aria" && <AriaPanel data={data} window={window} />}
      {ready && tab === "referrals" && <ReferralsPanel data={data} window={window} />}
    </>
  );
}

function TrendChart({ series, color, label = "Volume", testid }) {
  return (
    <div className="h-48" data-testid={testid}>
      <ResponsiveContainer width="100%" height="100%">
        <LineChart data={series} margin={{ top: 8, right: 12, left: -10, bottom: 0 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="#eee" />
          <XAxis dataKey="date" tick={TICK_STYLE} tickFormatter={(d) => d.slice(5)} interval={Math.ceil(series.length / 8)} />
          <YAxis tick={TICK_STYLE} allowDecimals={false} />
          <Tooltip contentStyle={{ fontSize: 12 }} />
          <Line type="monotone" dataKey="count" name={label} stroke={color} strokeWidth={2} dot={false} activeDot={{ r: 4 }} />
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
}

function PriorAuthPanel({ data, window }) {
  const k = data.kpis;
  return (
    <>
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-5">
        <KpiCard label="Submissions" value={k.total} testid="pa-total" />
        <KpiCard label="Approved" value={k.approved} accent testid="pa-approved" />
        <KpiCard label="Approval Rate" value={`${k.approval_rate}%`} testid="pa-approval-rate" />
        <KpiCard label="Denied" value={k.denied} testid="pa-denied" />
        <KpiCard label="Avg Days to Decision" value={k.avg_days_to_decision} testid="pa-avg-days" />
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-5">
        <SectionCard title={`Submission Volume — last ${window} days`} testid="pa-trend">
          <TrendChart series={data.daily_series} color="#4A0E8F" label="Submissions" testid="pa-trend-chart" />
        </SectionCard>
        <SectionCard title="Top Payers" testid="pa-payers">
          <Bars data={data.payer_breakdown} xKey="payer" color="#9B59B6" />
        </SectionCard>
        <SectionCard title="Top Denial Reasons" testid="pa-denials">
          {data.denial_reasons.length === 0 ? <Empty /> : (
            <ul className="space-y-2 text-sm">
              {data.denial_reasons.map((d, i) => (
                <li key={i} className="flex justify-between text-brand-text">
                  <span>{d.reason}</span>
                  <span className="font-semibold text-red-600">{d.count}</span>
                </li>
              ))}
            </ul>
          )}
        </SectionCard>
      </div>
      <SectionCard title="Recent Submissions" testid="pa-records">
        <RecordsTable rows={data.records} cols={[
          { key: "submitted_date", label: "Submitted", fmt: fmtDate },
          { key: "payer_name", label: "Payer" },
          { key: "cpt", label: "CPT" },
          { key: "icd10", label: "ICD-10" },
          { key: "status", label: "Status", fmt: Pill },
          { key: "days_to_decision", label: "Days→Decision", fmt: (v) => v ?? "—" },
          { key: "denial_reason", label: "Denial Reason", fmt: (v) => v || "—" },
          { key: "appeal_outcome", label: "Appeal", fmt: (v) => v || "—" },
        ]} />
      </SectionCard>
    </>
  );
}

function CptPanel({ data, window }) {
  const k = data.kpis;
  return (
    <>
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-3 mb-5">
        <KpiCard label="Codings" value={k.total} testid="cpt-total" />
        <KpiCard label="ARIA Match Rate" value={`${k.code_match_rate}%`} testid="cpt-match" />
        <KpiCard label="Acceptance Rate" value={`${k.acceptance_rate}%`} accent testid="cpt-acceptance" />
        <KpiCard label="Accepted" value={k.accepted} testid="cpt-accepted" />
        <KpiCard label="Rejected" value={k.rejected} testid="cpt-rejected" />
        <KpiCard label="Variance Flags" value={k.variance_flagged} testid="cpt-variance" />
        <KpiCard label="Revenue Gap" value={`$${(k.revenue_gap || 0).toLocaleString()}`} testid="cpt-gap" />
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-5">
        <SectionCard title={`Coding Volume — last ${window} days`} testid="cpt-trend">
          <TrendChart series={data.daily_series} color="#0D7377" label="Codings" testid="cpt-trend-chart" />
        </SectionCard>
        <SectionCard title="Per-Code Performance" testid="cpt-perf">
          {data.code_performance.length === 0 ? <Empty /> : (
            <div className="overflow-x-auto">
              <table className="w-full text-xs" data-testid="cpt-perf-table">
                <thead>
                  <tr className="text-brand-muted border-b border-brand">
                    <th className="text-left py-2">Code</th>
                    <th className="text-right">Total</th>
                    <th className="text-right">Accepted</th>
                    <th className="text-right">Rejected</th>
                    <th className="text-right">Variance</th>
                    <th className="text-right">Acc Rate</th>
                  </tr>
                </thead>
                <tbody>
                  {data.code_performance.map((r, i) => (
                    <tr key={i} className="border-b border-brand last:border-0">
                      <td className="py-1.5 font-mono">{r.code}</td>
                      <td className="text-right">{r.total}</td>
                      <td className="text-right text-emerald-700">{r.accepted}</td>
                      <td className="text-right text-red-600">{r.rejected}</td>
                      <td className="text-right text-amber-700">{r.variance}</td>
                      <td className="text-right font-semibold">{r.acceptance_rate}%</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </SectionCard>
      </div>
      <SectionCard title="Recent Codings (ARIA suggestion vs submitted)" testid="cpt-records">
        <RecordsTable rows={data.records} cols={[
          { key: "timestamp", label: "Date", fmt: fmtDate },
          { key: "aria_suggested_code", label: "ARIA Suggested", fmt: (v) => <span className="font-mono">{v}</span> },
          { key: "submitted_code", label: "Submitted", fmt: (v) => <span className="font-mono">{v}</span> },
          { key: "code_match", label: "Match", fmt: (v) => v ? "✓" : "✗" },
          { key: "icd10", label: "ICD-10" },
          { key: "payer_name", label: "Payer" },
          { key: "expected_reimbursement", label: "Expected", fmt: (v) => v ? `$${v.toFixed(2)}` : "—" },
          { key: "reimbursement_received", label: "Received", fmt: (v) => v != null ? `$${v.toFixed(2)}` : "pending" },
          { key: "variance_flagged", label: "Flag", fmt: (v) => v ? <AlertTriangle className="w-3.5 h-3.5 text-amber-600 inline" /> : "—" },
        ]} />
      </SectionCard>
    </>
  );
}

function AriaPanel({ data, window }) {
  const k = data.kpis;
  return (
    <>
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3 mb-5">
        <KpiCard label="Queries" value={k.total_queries} testid="aria-total" />
        <KpiCard label="Success Rate" value={`${k.success_rate}%`} accent testid="aria-success" />
        <KpiCard label="Avg Response" value={`${k.avg_response_ms}ms`} testid="aria-rt" />
        <KpiCard label="👍 Up" value={k.thumbs_up} testid="aria-up" />
        <KpiCard label="👎 Down" value={k.thumbs_down} testid="aria-down" />
        <KpiCard label="Clarifications" value={k.clarifications} testid="aria-clar" />
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-5">
        <SectionCard title={`Query Volume — last ${window} days`} testid="aria-trend">
          <TrendChart series={data.daily_series} color="#9B59B6" label="Queries" testid="aria-trend-chart" />
        </SectionCard>
        <SectionCard title="By Module" testid="aria-modules">
          <Bars data={data.by_module} xKey="module" color="#4A0E8F" />
        </SectionCard>
        <SectionCard title="By Intent" testid="aria-intents">
          <Bars data={data.by_intent} xKey="intent" color="#0D7377" />
        </SectionCard>
      </div>
      <SectionCard title="Recent ARIA Queries (de-identified)" testid="aria-records">
        <RecordsTable rows={data.records} cols={[
          { key: "timestamp", label: "Time", fmt: fmtDate },
          { key: "query", label: "Query" },
          { key: "module", label: "Module" },
          { key: "intent", label: "Intent" },
          { key: "outcome", label: "Outcome", fmt: Pill },
          { key: "response_time_ms", label: "RT(ms)" },
          { key: "feedback", label: "Feedback", fmt: (v) => v || "—" },
        ]} />
      </SectionCard>
    </>
  );
}

function ReferralsPanel({ data, window }) {
  const k = data.kpis;
  return (
    <>
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-3 mb-5">
        <KpiCard label="Sent" value={k.total} testid="ref-total" />
        <KpiCard label="Appt Scheduled" value={`${k.appointment_scheduled_rate}%`} testid="ref-sched" />
        <KpiCard label="Specialist Responded" value={`${k.specialist_response_rate}%`} testid="ref-resp" />
        <KpiCard label="Patient Followed Up" value={`${k.patient_followup_rate}%`} testid="ref-follow" />
        <KpiCard label="Completion Rate" value={`${k.completion_rate}%`} accent testid="ref-complete" />
        <KpiCard label="No Response" value={k.no_response_count} testid="ref-noresp" />
        <KpiCard label="Avg Cycle Days" value={k.avg_cycle_days} testid="ref-cycle" />
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-5">
        <SectionCard title={`Referral Volume — last ${window} days`} testid="ref-trend">
          <TrendChart series={data.daily_series} color="#7E22CE" label="Referrals Sent" testid="ref-trend-chart" />
        </SectionCard>
        <SectionCard title="By Specialty" testid="ref-spec">
          <Bars data={data.by_specialty} xKey="specialty" color="#9B59B6" />
        </SectionCard>
      </div>
      <SectionCard title="Recent Referrals" testid="ref-records">
        <RecordsTable rows={data.records} cols={[
          { key: "sent_date", label: "Sent", fmt: fmtDate },
          { key: "to_specialty", label: "Specialty" },
          { key: "diagnosis", label: "Diagnosis" },
          { key: "status", label: "Status", fmt: Pill },
          { key: "appointment_scheduled", label: "Appt", fmt: YN },
          { key: "specialist_responded", label: "Resp", fmt: YN },
          { key: "patient_followed_up", label: "F/U", fmt: YN },
          { key: "cycle_days", label: "Days", fmt: (v) => v ?? "—" },
        ]} />
      </SectionCard>
    </>
  );
}

function Bars({ data, xKey, color }) {
  if (!data || data.length === 0) return <Empty />;
  return (
    <div className="h-48">
      <ResponsiveContainer width="100%" height="100%">
        <BarChart data={data} margin={{ top: 8, right: 12, left: -20, bottom: 0 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="#eee" />
          <XAxis dataKey={xKey} tick={TICK_STYLE} interval={0} angle={-15} textAnchor="end" height={50} />
          <YAxis tick={TICK_STYLE} allowDecimals={false} />
          <Tooltip contentStyle={{ fontSize: 12 }} />
          <Bar dataKey="count" fill={color} radius={[4, 4, 0, 0]} />
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
}

function RecordsTable({ rows, cols }) {
  if (!rows || rows.length === 0) return <Empty />;
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-xs">
        <thead>
          <tr className="text-brand-muted border-b border-brand">
            {cols.map(c => <th key={c.key} className="text-left py-2 px-2 whitespace-nowrap">{c.label}</th>)}
          </tr>
        </thead>
        <tbody>
          {rows.slice(0, 25).map((r, i) => (
            <tr key={r.id || i} className="border-b border-brand last:border-0 hover:bg-brand-bg">
              {cols.map(c => (
                <td key={c.key} className="py-1.5 px-2 whitespace-nowrap text-brand-text">
                  {c.fmt ? c.fmt(r[c.key], r) : (r[c.key] ?? "—")}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

const Empty = () => <div className="text-xs text-brand-muted py-8 text-center">No data in this window.</div>;
const fmtDate = (v) => v ? new Date(v).toLocaleDateString("en-GB", { day: "2-digit", month: "short" }) : "—";
const YN = (v) => v === true ? <span className="text-emerald-700 font-semibold">Y</span> : v === false ? <span className="text-red-600 font-semibold">N</span> : <span className="text-brand-muted">—</span>;
const Pill = (v) => {
  if (!v) return "—";
  const map = {
    approved: "bg-emerald-50 text-emerald-700 border-emerald-200",
    denied: "bg-red-50 text-red-700 border-red-200",
    pending: "bg-amber-50 text-amber-700 border-amber-200",
    completed: "bg-emerald-50 text-emerald-700 border-emerald-200",
    scheduled: "bg-blue-50 text-blue-700 border-blue-200",
    sent: "bg-purple-50 text-purple-700 border-purple-200",
    accepted: "bg-emerald-50 text-emerald-700 border-emerald-200",
    no_response: "bg-red-50 text-red-700 border-red-200",
    success: "bg-emerald-50 text-emerald-700 border-emerald-200",
    failed: "bg-red-50 text-red-700 border-red-200",
    clarification: "bg-amber-50 text-amber-700 border-amber-200",
    navigation: "bg-purple-50 text-purple-700 border-purple-200",
  };
  return <span className={`px-2 py-0.5 rounded-full border text-[10px] uppercase font-semibold ${map[v] || "bg-gray-50 text-gray-600 border-gray-200"}`}>{v.replace("_", " ")}</span>;
};
